import { createCoreApiClient } from '@washed/api-client';

export type SponsorPayPlanCode = 'S1' | 'S2' | 'D1' | 'D2' | 'F1' | 'F2';
export type SponsorInviteStatus = 'accepted' | 'cancelled' | 'expired' | 'open';
export type SponsorPaymentNetwork = 'apple_pay' | 'card' | 'google_pay';

export interface SponsorInviteSummary {
  readonly currency: 'XOF';
  readonly expiresAt: string;
  readonly internationalFeeMinor: number;
  readonly planAmountMinor: number;
  readonly planCode: SponsorPayPlanCode;
  readonly recipientFirstName: string;
  readonly status: SponsorInviteStatus;
  readonly totalAmountMinor: number;
}

export interface SponsorCheckoutInput {
  readonly cancelUrl: string;
  readonly payerCurrency: string;
  readonly requestedNetwork: SponsorPaymentNetwork;
  readonly returnUrl: string;
  readonly sponsorEmail: string;
  readonly taxCountry: string;
  readonly taxPostalCode: string;
}

export interface SponsorCheckoutLineItem {
  readonly amountMinor: number;
  readonly currency: string;
  readonly label: 'Plan';
}

export interface SponsorCheckoutSession {
  readonly checkoutUrl: string;
  readonly currency: string;
  readonly expiresAt: string;
  readonly internationalFeeMinor: number;
  readonly lineItems: readonly SponsorCheckoutLineItem[];
  readonly planAmountMinor: number;
  readonly providerReference: string;
  readonly sessionId: string;
  readonly taxAmountExclusiveMinor: number;
  readonly taxAmountInclusiveMinor: number;
  readonly taxBehavior: 'inclusive';
  readonly totalAmountMinor: number;
}

export interface SponsorQuote {
  readonly currency: string;
  readonly internationalFeeMinor: number;
  readonly lineItems: readonly SponsorCheckoutLineItem[];
  readonly planAmountMinor: number;
  readonly quoteExact: boolean;
  readonly taxAmountExclusiveMinor: number;
  readonly taxAmountInclusiveMinor: number;
  readonly taxBehavior: 'inclusive';
  readonly totalAmountMinor: number;
}

export interface SponsorTaxLocationInput {
  readonly taxCountry: string;
  readonly taxPostalCode: string;
}

export interface SponsorPayApi {
  readonly getInvite: (token: string) => Promise<SponsorInviteSummary>;
  readonly getQuote: (
    token: string,
    payerCurrency: string,
    taxLocation: SponsorTaxLocationInput,
  ) => Promise<SponsorQuote>;
  readonly startCheckout: (
    token: string,
    input: SponsorCheckoutInput,
  ) => Promise<SponsorCheckoutSession>;
}

interface SponsorPayApiOptions {
  readonly baseUrl?: string;
  readonly fetch?: typeof fetch;
}

export function createSponsorPayApi(options: SponsorPayApiOptions = {}): SponsorPayApi {
  const client = () =>
    createCoreApiClient({
      baseUrl: requireCoreApiBaseUrl(options.baseUrl),
      fetch: options.fetch ?? fetch,
    });

  return {
    async getInvite(token) {
      return client().request<SponsorInviteSummary>('getSponsorInvite', {
        pathParams: { token },
      });
    },
    async getQuote(token, payerCurrency, taxLocation) {
      return client().request<SponsorQuote>('getSponsorInviteQuote', {
        pathParams: { token },
        query: {
          payerCurrency,
          taxCountry: taxLocation.taxCountry,
          taxPostalCode: taxLocation.taxPostalCode,
        },
      });
    },
    async startCheckout(token, input) {
      return client().request<SponsorCheckoutSession>('startSponsorInviteCheckout', {
        body: input,
        pathParams: { token },
      });
    },
  };
}

export const sponsorPayApi = createSponsorPayApi();

function requireCoreApiBaseUrl(explicitBaseUrl: string | undefined): string {
  const baseUrl = explicitBaseUrl ?? import.meta.env.VITE_CORE_API_BASE_URL;

  if (typeof baseUrl !== 'string' || baseUrl.trim().length === 0) {
    throw new Error('VITE_CORE_API_BASE_URL is required for Sponsor Pay.');
  }

  return baseUrl.trim();
}
